import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Chip,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
} from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useState } from "react";
import SimpleBar from "components/third-party/SimpleBar";
import { PieChartOutlined, TableOutlined } from "@ant-design/icons";
import { IInstituteWiseMaterialResult } from "features/libraryDashboard/api/types";

type Props = {
  data: IInstituteWiseMaterialResult[];
};

const InstituteWiseMaterial = ({ data }: Props) => {
  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#9d3be3",
    "#FFBB28",
    "#FF8042",
    "#52D726",
    "#FFEC00",
    "#FF7300",
    "#007ED6",
    "#7CDDDD",
    "#64C2A6",
    "#FF2E7E",
  ];

  const [view, setView] = useState("Visual");
  const titleData =
    data?.map((volume) => ({
      name: volume.instituteName,
      value: volume.title,
    })) || [];

  const volumeData =
    data?.map((volume) => ({
      name: volume.instituteName,
      value: volume.volume,
    })) || [];

  const legendData = [
    ...titleData.map((item, index) => ({
      name: item.name,
      color: COLORS[index % COLORS.length],
    })),
  ];

  const CustomLegend = () => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 14px",
        alignItems: "center",
        width: "100%",
        maxHeight: 120,
        overflowY: "auto",
        padding: "4px 0",
      }}
    >
      {legendData?.map((item, index) => (
        <div
          key={`legend-item-${index}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              flexShrink: 0,
              borderRadius: "50%",
              backgroundColor: item.color,
            }}
          ></div>
          <Typography
            variant="body2"
            sx={{ wordBreak: "break-word", fontSize: "12px" }}
          >
            {item.name}
          </Typography>
        </div>
      ))}
    </div>
  );

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardHeader
        sx={{
          p: 2,
          flexWrap: "wrap",
          rowGap: 1,
          "& .MuiCardHeader-action": { alignSelf: "center", m: 0 },
        }}
        title="Institute wise material"
        action={
          <Grid container spacing={1} flexWrap="wrap">
            <Grid item>
              <Chip
                icon={<PieChartOutlined />}
                label="Visual"
                color={view === "Visual" ? "primary" : "default"}
                onClick={() => setView("Visual")}
              />
            </Grid>
            <Grid item>
              <Chip
                label="Tabular"
                icon={<TableOutlined />}
                color={view === "Tabular" ? "primary" : "default"}
                onClick={() => setView("Tabular")}
              />
            </Grid>
          </Grid>
        }
      />
      <Divider />
      <CardContent>
        {view === "Visual" && (
          <>
            <Grid
              container
              rowGap={1}
              sx={{
                padding: "10px 20px",
                borderRadius: "8px",
                backgroundColor: "#fff",
                alignItems: "center",
              }}
            >
              {data && data.length > 0 ? (
                <Grid item xs={6}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#757575",
                    }}
                  >
                    Accessions
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      fontSize: "20px",
                    }}
                  >
                    {data && data.length > 0
                      ? data[0].totalVolume.toLocaleString("en-IN")
                      : "No Accessions Available"}
                  </Typography>
                </Grid>
              ) : (
                <></>
              )}
              {data && data.length > 0 ? (
                <Grid item xs={6}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#757575",
                    }}
                  >
                    Material
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      fontSize: "20px",
                    }}
                  >
                    {data && data.length > 0
                      ? data[0].totalTitle.toLocaleString("en-IN")
                      : "No Material Available"}
                  </Typography>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} lg={8}>
                <ResponsiveContainer height={200}>
                  <PieChart>
                    <Pie
                      data={titleData}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      fill="#8884d8"
                      minAngle={10}
                    >
                      {titleData?.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Pie
                      data={volumeData}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={100}
                      fill="#82ca9d"
                      minAngle={20}
                    >
                      {volumeData?.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12} lg={4}>
                <CustomLegend />
              </Grid>
            </Grid>
          </>
        )}

        {view === "Tabular" && (
          <TableContainer sx={{ mt: 0, maxHeight: 340 }}>
            <SimpleBar sx={{ height: 290 }}>
              <Table size="small">
                <TableHead
                  sx={{
                    position: "sticky",
                    top: -1,
                    zIndex: 2,
                  }}
                >
                  <TableRow>
                    <TableCell align="left">Institute</TableCell>
                    <TableCell align="right">Title</TableCell>
                    <TableCell align="right">Volume</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.map((row) => (
                    <TableRow hover key={row.instituteID}>
                      <TableCell align="left">{row.instituteName}</TableCell>
                      <TableCell align="right">
                        {row.title.toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell align="right">
                        {row.volume.toLocaleString("en-IN")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SimpleBar>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default InstituteWiseMaterial;
